import type { Locale } from "@chams-pay/contracts";
import { useLayoutEffect, useRef } from "react";
import type { ReactNode } from "react";

const A4_WIDTH_CSS_PIXELS = (210 / 25.4) * 96;
const MINIMUM_ZOOM = 0.5;
const MAXIMUM_ZOOM = 3;

export function clampDocumentPreviewZoom(value: number) {
  return Math.min(MAXIMUM_ZOOM, Math.max(MINIMUM_ZOOM, value));
}

export function documentPreviewWheelZoom(currentZoom: number, deltaY: number) {
  return clampDocumentPreviewZoom(currentZoom * Math.exp(-deltaY * 0.002));
}

export function isDocumentPreviewZoomWheel(ctrlKey: boolean, metaKey: boolean) {
  return ctrlKey || metaKey;
}

export function documentPreviewPinchZoom(
  startZoom: number,
  startDistance: number,
  currentDistance: number,
) {
  if (startDistance <= 0) return clampDocumentPreviewZoom(startZoom);
  return clampDocumentPreviewZoom(startZoom * (currentDistance / startDistance));
}

type PreviewAnchor = Readonly<{ clientX: number; clientY: number }>;

export function A4DocumentPreview({
  children,
  kind = "receipt",
  locale,
  spatialDirection = "document",
}: {
  readonly children: ReactNode;
  readonly kind?: "receipt" | "report";
  readonly locale: Locale;
  readonly spatialDirection?: "document" | "ltr";
}) {
  const previewRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const preview = previewRef.current;
    const stage = stageRef.current;
    const content = contentRef.current;
    if (!preview || !stage || !content) return;
    const canvas = preview.closest<HTMLElement>("[data-document-preview-canvas]") ?? preview;

    let userZoom = 1;
    let pinchStartDistance = 0;
    let pinchStartZoom = 1;

    const previewCenter = (): PreviewAnchor => {
      const rect = canvas.getBoundingClientRect();
      return {
        clientX: rect.left + rect.width / 2,
        clientY: rect.top + rect.height / 2,
      };
    };

    const scrollAncestor = (axis: "x" | "y") => {
      let element: HTMLElement | null = canvas;
      while (element) {
        const styles = getComputedStyle(element);
        const overflow = axis === "x" ? styles.overflowX : styles.overflowY;
        const scrollable = overflow === "auto" || overflow === "scroll";
        const hasOverflow =
          axis === "x"
            ? element.scrollWidth > element.clientWidth
            : element.scrollHeight > element.clientHeight;
        if (scrollable && hasOverflow) return element;
        element = element.parentElement;
      }
      return document.scrollingElement instanceof HTMLElement ? document.scrollingElement : null;
    };

    const applyScale = (anchor?: PreviewAnchor) => {
      const previousRect = stage.getBoundingClientRect();
      const relativeAnchor =
        anchor && previousRect.width && previousRect.height
          ? {
              x: (anchor.clientX - previousRect.left) / previousRect.width,
              y: (anchor.clientY - previousRect.top) / previousRect.height,
            }
          : null;
      const fitScale = Math.min(1, preview.clientWidth / A4_WIDTH_CSS_PIXELS);
      const scale = fitScale * userZoom;
      const baseHeight = content.scrollHeight;

      stage.style.inlineSize = `${A4_WIDTH_CSS_PIXELS * scale}px`;
      stage.style.blockSize = `${baseHeight * scale}px`;
      content.style.setProperty("--a4-document-preview-scale", String(scale));

      if (anchor && relativeAnchor) {
        const nextRect = stage.getBoundingClientRect();
        const deltaX = nextRect.left + nextRect.width * relativeAnchor.x - anchor.clientX;
        const deltaY = nextRect.top + nextRect.height * relativeAnchor.y - anchor.clientY;
        const horizontalScroller = scrollAncestor("x");
        const verticalScroller = scrollAncestor("y");
        if (horizontalScroller) horizontalScroller.scrollLeft += deltaX;
        if (verticalScroller) verticalScroller.scrollTop += deltaY;
      }
    };

    const setZoom = (value: number, anchor: PreviewAnchor = previewCenter()) => {
      const nextZoom = clampDocumentPreviewZoom(value);
      if (Math.abs(nextZoom - userZoom) < 0.001) return;
      userZoom = nextZoom;
      applyScale(anchor);
    };

    const onWheel = (event: WheelEvent) => {
      if (!isDocumentPreviewZoomWheel(event.ctrlKey, event.metaKey)) return;
      event.preventDefault();
      setZoom(documentPreviewWheelZoom(userZoom, event.deltaY), event);
    };
    const touchDistance = (touches: TouchList) =>
      Math.hypot(
        touches[0]!.clientX - touches[1]!.clientX,
        touches[0]!.clientY - touches[1]!.clientY,
      );
    const touchCenter = (touches: TouchList): PreviewAnchor => ({
      clientX: (touches[0]!.clientX + touches[1]!.clientX) / 2,
      clientY: (touches[0]!.clientY + touches[1]!.clientY) / 2,
    });
    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 2) return;
      pinchStartDistance = touchDistance(event.touches);
      pinchStartZoom = userZoom;
    };
    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 2 || !pinchStartDistance) return;
      event.preventDefault();
      setZoom(
        documentPreviewPinchZoom(pinchStartZoom, pinchStartDistance, touchDistance(event.touches)),
        touchCenter(event.touches),
      );
    };
    const endPinch = (event: TouchEvent) => {
      if (event.touches.length < 2) pinchStartDistance = 0;
    };
    const onResize = () => applyScale();

    applyScale();
    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("touchstart", onTouchStart, { passive: true });
    canvas.addEventListener("touchmove", onTouchMove, { passive: false });
    canvas.addEventListener("touchend", endPinch, { passive: true });
    canvas.addEventListener("touchcancel", endPinch, { passive: true });

    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => applyScale());
    observer?.observe(preview);
    observer?.observe(content);
    if (!observer) window.addEventListener("resize", onResize);

    return () => {
      observer?.disconnect();
      if (!observer) window.removeEventListener("resize", onResize);
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", endPinch);
      canvas.removeEventListener("touchcancel", endPinch);
    };
  }, []);

  return (
    <div
      ref={previewRef}
      className="a4-document-preview"
      dir={spatialDirection === "ltr" ? "ltr" : locale === "ar" ? "rtl" : "ltr"}
      data-document-kind={kind}
    >
      <div ref={stageRef} className="a4-document-preview__stage">
        <div ref={contentRef} className="a4-document-preview__content">
          {kind === "receipt" ? (
            <div className="a4-document-preview__paper" dir={locale === "ar" ? "rtl" : "ltr"}>
              {children}
            </div>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
}
